<template>
    <ContentComp
        ref="content"
        class="vh-50 p-0"
        :load-state="LoadState"
    >
        <GridComp
            ref="grid"
            :gridSetup="gridSetup"
            class="overflow-auto h-100"
        />
    </ContentComp>
</template>
<script setup lang="ts">
import { DateComp, GridComp } from '@/components';
import { GridColumnDefinition, GridDataSource, GridSetup, type IGridCompExposed } from '@/components/Grid/Classes';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import ContentComp from '@/components/layout/ContentComp.vue';
import { useTaskLoader } from '@/composeables';
import { TransactionDao } from '@/dao';
import { Transaction } from '@/objects/generated';
import { propertyof } from '@/utilities';
import { useElementVisibility } from '@vueuse/core';
import { watch } from 'vue';
import { ref } from 'vue';

const props = defineProps<{
    deviceId: string;
}>();

// The grid comp
const grid = ref<IGridCompExposed<Transaction> | null>(null);

const data = ref<Transaction[]>([]);

const content = ref<HTMLElement | null>(null);

const targetIsVisible = useElementVisibility(content);

const { AddLoader, LoadState } = useTaskLoader();

/**
 * Set up the datasource
 */
const dataSource = new GridDataSource<Transaction>({
    totalNumberOfRows: () => data.value.length, // Maybe this needs to change for a server side data source

    rowGetter: (params: IGridRowGetterParams<Transaction>) => {
        const slice = data.value.slice(params.startRow, params.endRow);

        params.successCallback(slice, data.value, data.value.length);
    },
});

/**
 * Columns
 */
const gridColumnDefinitions = [
    new GridColumnOptionsBuilder<Transaction>(propertyof<Transaction>('Created'), 'general.crtime')
        .Size(200, 200)
        .CellRenderer(DateComp, { format: 'DATETIMEMS3' })
        .ReadOnly()
        .Build(),
    new GridColumnOptionsBuilder<Transaction>(propertyof<Transaction>('TransactionTime'), 'transaction.transactiontime')
        .Size(200, 200)
        .CellRenderer(DateComp, { format: 'DATETIMEMS3' })
        .ReadOnly()
        .Build(),
    new GridColumnOptionsBuilder<Transaction>(propertyof<Transaction>('Type'), 'transaction.transactiontype')
        .SortDisabled()
        .Size(80, 80)
        .ReadOnly()
        .Build(),
    new GridColumnOptionsBuilder<Transaction>(propertyof<Transaction>('Content'), 'transaction.content').ReadOnly().Build(),
    new GridColumnOptionsBuilder<Transaction>(propertyof<Transaction>('Messages'), 'transaction.messages').ReadOnly().Build(),
];

/**
 * Set up the grid
 */
const gridSetup = new GridSetup<Transaction>({
    rowIdGetter: (row: Transaction) => row.ID.toString(),
    datasource: dataSource,
    GridColumnDefinition: new GridColumnDefinition({
        ColumnOptions: gridColumnDefinitions,
    }),
    rowSelection: 'single',
    gridkey: 'deviceloggrid',
});

const loadData = async () => {
    AddLoader(async () => {
        // Fetch the data
        data.value = await TransactionDao.GetTransactionsForDevice(props.deviceId);
        grid.value?.purgeCachedData();
    }, '');
};

watch(targetIsVisible, async () => {
    if (!content.value) return;

    if (targetIsVisible.value) {
        loadData();
    }
});
</script>
